<!-- Call overlay — visible whenever a call session exists -->
<ng-container *ngIf="callSession$ | async as session">

  <!-- ── Error toast ──────────────────────────────────────────────────────── -->
  <div *ngIf="errorMessage"
       class="fixed top-6 left-1/2 -translate-x-1/2 z-[10000] rounded-xl bg-error-600 px-5 py-3 text-sm font-medium text-white shadow-lg">
    {{ errorMessage }}
  </div>

  <!-- ══════════════════════════════════════════════════════════════
       INCOMING CALL BANNER  (ringing state)
  ══════════════════════════════════════════════════════════════ -->
  <div *ngIf="session.state === 'ringing'"
       class="fixed bottom-6 right-6 z-[9999] w-80 overflow-hidden rounded-2xl bg-gray-900 shadow-2xl ring-1 ring-white/10">

    <div class="relative flex flex-col items-center gap-4 p-6">
      <!-- Avatar with ripple -->
      <div class="relative">
        <span class="absolute inset-0 animate-ping rounded-full bg-brand-500/30"></span>
        <span class="absolute -inset-2 animate-ping rounded-full bg-brand-500/15 [animation-delay:0.25s]"></span>
        <img [src]="getAvatar(session)" [alt]="session.remoteUser.displayName"
             class="relative h-16 w-16 rounded-full object-cover ring-2 ring-brand-400">
      </div>

      <!-- Caller info -->
      <div class="text-center">
        <p class="text-xs font-semibold uppercase tracking-widest text-brand-400">
          {{ session.callType === 'VIDEO' ? '📹 Video Call' : '📞 Voice Call' }}
        </p>
        <h4 class="mt-1 text-base font-semibold text-white">{{ session.remoteUser.displayName }}</h4>
        <p class="text-sm text-gray-400">is calling you…</p>
      </div>

      <!-- Accept / Decline -->
      <div class="mt-2 flex gap-6">
        <!-- Decline -->
        <button (click)="reject()" class="flex flex-col items-center gap-1.5">
          <span class="flex h-14 w-14 items-center justify-center rounded-full bg-error-600 shadow-lg transition-colors hover:bg-error-700">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.05 12.05 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 002.59 3.4z"
                    stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    transform="rotate(135 12 12)"/>
            </svg>
          </span>
          <span class="text-xs text-gray-400">Decline</span>
        </button>

        <!-- Accept -->
        <button (click)="answer()" class="flex flex-col items-center gap-1.5">
          <span class="flex h-14 w-14 items-center justify-center rounded-full bg-success-500 shadow-lg transition-colors hover:bg-success-600">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"
                    stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="text-xs text-gray-400">Accept</span>
        </button>
      </div>
    </div>
  </div>

  <!-- ══════════════════════════════════════════════════════════════
       ACTIVE CALL  (calling / connected states)
  ══════════════════════════════════════════════════════════════ -->
  <div *ngIf="session.state === 'calling' || session.state === 'connected'"
       class="fixed inset-0 z-[9998] flex flex-col bg-gray-950">

    <!-- ── Video / Avatar area ───────────────────────────────────── -->
    <div class="relative flex-1 overflow-hidden bg-gray-900">

      <!--
        BUG FIX 4: Remote video element always present in DOM for video calls,
        but hidden via CSS until we actually have a video track.
        Using [hidden] instead of *ngIf so the @ViewChild ref stays stable.
      -->
      <!--
        Always keep this element mounted while calling/connected — even for
        voice calls — since it's what plays the remote MediaStream's audio
        track. Only its visual display is toggled; a hidden <video> still
        plays audio.
      -->
      <video #remoteVideo
             autoplay
             playsinline
             [hidden]="!isVideo(session) || !hasRemoteVideo"
             class="h-full w-full object-cover">
      </video>

      <!-- Avatar fallback: voice call OR video call waiting for stream -->
      <div *ngIf="!isVideo(session) || !hasRemoteVideo"
           class="flex h-full flex-col items-center justify-center gap-6">
        <div class="relative">
          <span class="absolute inset-0 animate-ping rounded-full bg-brand-500/20 [animation-duration:1.5s]"></span>
          <img [src]="getAvatar(session)" [alt]="session.remoteUser.displayName"
               class="relative h-28 w-28 rounded-full object-cover ring-4 ring-white/20">
        </div>
        <div class="text-center">
          <h3 class="text-xl font-semibold text-white">{{ session.remoteUser.displayName }}</h3>
          <p class="mt-1 text-sm text-gray-400">{{ getCallLabel(session) }}</p>
        </div>
      </div>

      <!-- Local video PiP (video calls only, visible once stream exists) -->
      <video #localVideo
             *ngIf="isVideo(session) && (localStream$ | async)"
             autoplay
             playsinline
             muted
             [hidden]="session.isCameraOff"
             class="absolute bottom-4 right-4 h-32 w-24 rounded-xl object-cover shadow-lg ring-2 ring-white/20">
      </video>

      <!-- Call timer -->
      <div *ngIf="session.state === 'connected'"
           class="absolute left-1/2 top-4 -translate-x-1/2">
        <span class="rounded-full bg-black/50 px-4 py-1.5 text-sm font-mono text-white backdrop-blur-sm">
          {{ callDuration }}
        </span>
      </div>
    </div>

    <!-- ── Controls ──────────────────────────────────────────────── -->
    <div class="flex items-center justify-center gap-5 bg-gray-950 px-8 py-6">

      <!-- Mute -->
      <button (click)="toggleMute()"
              [class.bg-white\/20]="!session.isMuted"
              [class.bg-error-600]="session.isMuted"
              class="flex h-14 w-14 items-center justify-center rounded-full transition-colors">
        <svg *ngIf="!session.isMuted" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <rect x="9" y="2" width="6" height="12" rx="3" stroke="white" stroke-width="1.5"/>
          <path d="M20 10C20 14.4183 16.4183 18 12 18C7.58172 18 4 14.4183 4 10M12 18V22M8 22H16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
        <svg *ngIf="session.isMuted" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 3L21 21M9 9V14C9 15.66 10.34 17 12 17C13.55 17 14.83 15.85 14.98 14.35M15 9.34V6C15 4.34 13.66 3 12 3C11.13 3 10.34 3.37 9.78 3.97" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
          <path d="M20 10C20 13.75 17.49 16.9 14.06 17.77M4 10C4 11.54 4.44 12.98 5.2 14.2M12 17V22M8 22H16" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </button>

      <!-- Camera toggle (video calls only) -->
      <button *ngIf="isVideo(session)"
              (click)="toggleCamera()"
              [class.bg-white\/20]="!session.isCameraOff"
              [class.bg-error-600]="session.isCameraOff"
              class="flex h-14 w-14 items-center justify-center rounded-full transition-colors">
        <svg *ngIf="!session.isCameraOff" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M4.25 5.25C3 5.25 2 6.26 2 7.5V16.5C2 17.74 3 18.75 4.25 18.75H15.25C16.49 18.75 17.5 17.74 17.5 16.5V15.4L20.11 16.95C20.95 17.44 22 16.84 22 15.87V8.13C22 7.16 20.95 6.56 20.11 7.05L17.5 8.6V7.5C17.5 6.26 16.49 5.25 15.25 5.25H4.25Z" fill="white"/>
        </svg>
        <svg *ngIf="session.isCameraOff" width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 3L21 21M17.5 8.6V7.5C17.5 6.26 16.49 5.25 15.25 5.25H6.81M2 7.5C2 6.26 3 5.25 4.25 5.25H4.5M2 16.5C2 17.74 3 18.75 4.25 18.75H15.25C16.49 18.75 17.5 17.74 17.5 16.5V15.4L20.11 16.95C20.95 17.44 22 16.84 22 15.87V11"
                stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- End / Cancel call -->
      <button (click)="session.state === 'calling' ? cancel() : end()"
              class="flex h-16 w-16 items-center justify-center rounded-full bg-error-600 shadow-xl transition-colors hover:bg-error-700">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12 12 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 002.59 3.4z"
                stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                transform="rotate(135 12 12)"/>
        </svg>
      </button>
    </div>
  </div>

</ng-container>

<!-- ── Call Ended Toast ────────────────────────────────────────────────────
     Shown in the centre of the screen for 4 s after a call ends.
     Lives OUTSIDE the ng-container so it persists after session is cleared.
──────────────────────────────────────────────────────────────────────────── -->
<div *ngIf="callEndedToast"
     class="fixed inset-0 z-[10001] flex items-center justify-center pointer-events-none">
  <div class="flex flex-col items-center gap-3 rounded-2xl bg-gray-900/95 px-10 py-7 shadow-2xl ring-1 ring-white/10 backdrop-blur-md
              animate-[fadeInScale_0.25s_ease-out]">

    <!-- Icon -->
    <span class="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
      <svg *ngIf="callEndedToast.callType === 'VIDEO'" width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd"
              d="M4.25 5.25C3 5.25 2 6.26 2 7.5V16.5C2 17.74 3 18.75 4.25 18.75H15.25C16.49 18.75 17.5 17.74 17.5 16.5V15.4L20.11 16.95C20.95 17.44 22 16.84 22 15.87V8.13C22 7.16 20.95 6.56 20.11 7.05L17.5 8.6V7.5C17.5 6.26 16.49 5.25 15.25 5.25H4.25Z"
              fill="white" opacity="0.7"/>
      </svg>
      <svg *ngIf="callEndedToast.callType !== 'VIDEO'" width="28" height="28" viewBox="0 0 24 24" fill="none">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"
              stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" opacity="0.7"/>
      </svg>
    </span>

    <!-- Duration line -->
    <p class="text-2xl font-semibold tracking-tight text-white">
      Call ended
    </p>

    <!-- Duration -->
    <p class="text-sm font-mono text-gray-300">
      {{ callEndedToast.duration }}
    </p>
  </div>
</div>